import axios, {AxiosStatic, InternalAxiosRequestConfig} from 'axios';
import {AccountService} from "./AccountService";
import {ApiRoutes} from "../routes/ApiRoutes";
import {RouteUtils} from "../routes/RouteUtils";
import {showNedry} from "../components/Nedry/showNedry";
import {BoLoginFlow} from "./BoLoginFlow";

let isRetrying = false;
let redirectSuppressed = false;

/**
 * Call this when bo-login fails to prevent reboRedirect() from triggering a redirect loop.
 * Stale tokens will cause 401s on other API calls; without suppression, the interceptor
 * would try to refresh → fail → reboRedirect() → legacy login → back to React with token → infinite loop.
 */
export function suppressBoLoginRedirect() {
    redirectSuppressed = true;
}

const Axios: AxiosStatic = axios.create({
    baseURL: ApiRoutes.APP_API_URL_BACKEND()
}) as AxiosStatic;


Axios.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    if (request && request.headers) {

        const accessToken = AccountService.getAccessToken();
        const refreshToken = AccountService.getRefreshToken();

        if (AccountService.isLogged() && request.url !== ApiRoutes.REACT_APP_URL_REFRESH_TOKEN) {

            const token = request.url === ApiRoutes.REACT_APP_URL_LOGOUT ? refreshToken : accessToken;

            request.headers.Authorization = 'Bearer ' + token;
        }

        //add url in request headers
        request.headers["X-Requested-With"] = (window.location.href);
    }

    return request;
})

let isRefreshing = false;
let refreshSubscribers: ((newToken: any) => void)[] = [];

// Auth endpoints answer 401/403 as a normal business result (bad/expired token...). They must never go
// through "refresh then redirect to the legacy": that is how a login used to be aborted by its own
// logout/refresh call.
const isAuthUrl = (url?: string) =>
    !!url && [
        ApiRoutes.REACT_APP_URL_REFRESH_TOKEN,
        ApiRoutes.REACT_APP_URL_LOGOUT,
        ApiRoutes.REACT_APP_URL_BO_LOGIN,
        ApiRoutes.REACT_APP_URL_AUTHENTICATE,
        ApiRoutes.REACT_APP_URL_GOOGLE_LOGIN,
    ].includes(url);
const apiInterceptor = Axios.interceptors.response.use(
    response => response,
    async (error) => {
        if (error.code === "ERR_CANCELED") {
            return Promise.reject(error);
        }

        const {config, response} = error;
        const originalRequest = config;

        if (response) {
            //if 400 error logout
            // if (response.status === 400) {
            //     if (!isRetrying) {
            //         isRetrying = true;
            //         AccountService.logout(false).then(() => {
            //             RouteUtils.reboRedirect();
            //         }) // Consider auto logout on 400 errors
            //
            //     }
            //     return Promise.reject(error);
            // }
            // While a bo-login is running any other 401 is expected (no session yet): let the login decide.
            if (response.status === 401 && !originalRequest._retry && !isAuthUrl(originalRequest.url) && !BoLoginFlow.isInProgress()) {
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        refreshSubscribers.push((accessToken) => {
                            originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
                            resolve(Axios(originalRequest));
                        });
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    const accessToken = await AccountService.refreshToken();
                    Axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken.accessToken}`;
                    refreshSubscribers.forEach(callback => callback(accessToken.accessToken));
                    refreshSubscribers = [];
                    return Axios(originalRequest);
                } catch (refreshError) {
                    console.error("Refresh token invalid:", refreshError);
                    if (!redirectSuppressed) {
                        RouteUtils.reboRedirect();
                    }
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }

            // Nedry greets forbidden (403) responses — "you didn't say the magic word".
            // Skip the auth endpoints: an expired refresh token / rejected login 403s here on the
            // way to a login redirect, and we don't want Nedry flashing during that re-auth flow.
            // Also skip requests flagged suppressNedry: some endpoints use 403 as an expected
            // business answer (e.g. the 3CX log-call gate) and show their own explanation.
            const authUrls = [ApiRoutes.REACT_APP_URL_REFRESH_TOKEN, ApiRoutes.REACT_APP_URL_AUTHENTICATE];
            if (response.status === 403 && !authUrls.includes(originalRequest?.url) && !originalRequest?.suppressNedry) {
                showNedry();
            }
        }

        return Promise.reject(error);
    }
);

function removeInterceptor() {
    Axios.interceptors.response.eject(apiInterceptor);
}

export default Axios;